import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import { fetchQuery, graphql } from 'relay-runtime';
import environment from 'api/environment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import ProfileIcon from 'sharedComponents/core/ProfileIcon';
import AnchoredPanel from 'sharedComponents/core/AnchoredPanel';
import { BookingDelegatePickerQueryResponse } from './__generated__/BookingDelegatePickerQuery.graphql';

// Shape shared with BookingModal state. `key`/`value` mirror the legacy
// search-result shape (name / TTC ID) so initialUser from the side-modal
// dispatch keeps working unchanged.
export type BookingDelegate = {
  uuid: string;
  key: string;
  value: string;
  email?: string | null;
  profileImageUrl?: string | null;
};

const query = graphql`
  query BookingDelegatePickerQuery($text: String) {
    delegates(filter: { name: $text }, page: { limit: 20 }) {
      edges {
        uuid
        TTC_ID
        firstName
        lastName
        email
        profileImageUrl
      }
    }
  }
`;

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginBottom: 10
  },
  tokenBox: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gridGap: 8,
    padding: 8,
    minHeight: 40,
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 8,
    cursor: 'text',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:focus-within': {
      borderColor: '#0b57ff',
      boxShadow: '0 0 0 3px rgba(11, 87, 255, 0.1)'
    }
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gridGap: 8,
    padding: '4px 10px 4px 5px',
    background: '#f8f9fb',
    border: '1px solid #ededed',
    borderRadius: 999,
    cursor: 'default'
  },
  chipName: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.colors.primaryBlack,
    whiteSpace: 'nowrap'
  },
  chipRemove: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 12,
    color: '#74777f',
    cursor: 'pointer',
    transition: 'color 0.15s',
    '&:hover': {
      color: theme.colors.primaryBlack
    }
  },
  tokenInput: {
    flex: 1,
    minWidth: 140,
    padding: '4px 6px',
    border: 'none',
    outline: 'none',
    fontSize: 14,
    color: theme.colors.primaryBlack,
    '&::placeholder': {
      color: '#9ea2ad'
    }
  },
  list: {
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 10,
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06)',
    padding: 6,
    overflow: 'hidden'
  },
  // Scrolling lives on an inner element so the scrollbar sits inside the
  // panel's padding instead of cutting across its rounded corners
  listScroll: {
    maxHeight: 260,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gridGap: 2,
    scrollbarWidth: 'thin',
    scrollbarColor: '#d7dae0 transparent',
    '&::-webkit-scrollbar': {
      width: 8
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#d7dae0',
      borderRadius: 999
    }
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gridGap: 12,
    padding: '8px 12px',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': {
      background: '#f8f9fb'
    }
  },
  name: {
    fontSize: 14,
    color: theme.colors.primaryBlack
  },
  message: {
    padding: '28px 14px',
    textAlign: 'center',
    color: '#6A728F',
    fontSize: 14
  }
}));

type Props = {
  selected: BookingDelegate[];
  setSelected: (delegates: BookingDelegate[]) => void;
};

function BookingDelegatePicker({ selected, setSelected }: Props) {
  const classes = useStyles();
  const [text, setText] = React.useState('');
  const [results, setResults] = React.useState<BookingDelegate[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const debouncer = React.useRef<number>();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  // Close the results dropdown when clicking anywhere outside the picker.
  // The panel is portaled to document.body, so check it separately.
  React.useEffect(() => {
    if (!open) return;
    const onMouseDown = (evt: MouseEvent) => {
      const target = evt.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const run = (queryText: string) => {
    setLoading(true);
    clearTimeout(debouncer.current);
    debouncer.current = setTimeout(async () => {
      try {
        // Omit the filter on an empty query: an empty-string ILIKE drops rows
        // with NULL name columns, matching how the other delegate lists behave.
        const data = (await fetchQuery(environment, query, {
          text: queryText.length > 0 ? queryText : undefined
        })) as BookingDelegatePickerQueryResponse;
        const edges = data?.delegates?.edges ?? [];
        setResults(
          edges
            .filter((edge): edge is NonNullable<typeof edge> => Boolean(edge))
            .map((edge) => ({
              uuid: edge.uuid ?? '',
              key: `${edge.firstName} ${edge.lastName}`,
              value: edge.TTC_ID ?? '',
              email: edge.email,
              profileImageUrl: edge.profileImageUrl
            }))
        );
      } catch (err) {
        console.error('Delegate search failed', err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300) as unknown as number;
  };

  React.useEffect(() => {
    run('');
    return () => clearTimeout(debouncer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedUUIDs = selected.map((delegate) => delegate.uuid);
  // Already-selected delegates show as chips in the input, so hide them
  // from the results list rather than greying them out
  const visibleResults = results.filter(
    (delegate) => !selectedUUIDs.includes(delegate.uuid)
  );

  const add = (delegate: BookingDelegate) => {
    setSelected([...selected, delegate]);
    if (text.length > 0) {
      setText('');
      run('');
    }
  };

  return (
    <div className={classes.container} ref={containerRef}>
      <div
        className={classes.tokenBox}
        onClick={() => {
          inputRef.current?.focus();
          setOpen(true);
        }}
      >
        {selected.map((delegate) => (
          <span key={delegate.uuid} className={classes.chip}>
            <ProfileIcon
              name={delegate.key}
              url={delegate.profileImageUrl ?? undefined}
              size={22}
            />
            <span className={classes.chipName}>{delegate.key}</span>
            <span
              className={classes.chipRemove}
              onClick={(evt) => {
                evt.stopPropagation();
                setSelected(
                  selected.filter((entry) => entry.uuid !== delegate.uuid)
                );
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </span>
          </span>
        ))}
        <input
          ref={inputRef}
          className={classes.tokenInput}
          placeholder={
            selected.length === 0 ? 'Search delegates by name' : undefined
          }
          value={text}
          onFocus={() => setOpen(true)}
          onChange={(evt) => {
            setText(evt.target.value);
            run(evt.target.value);
          }}
          onKeyDown={(evt) => {
            if (
              evt.key === 'Backspace' &&
              text.length === 0 &&
              selected.length > 0
            ) {
              setSelected(selected.slice(0, -1));
            }
          }}
        />
      </div>
      <AnchoredPanel anchorRef={containerRef} open={open} panelRef={panelRef}>
        <div className={classes.list}>
          <div className={classes.listScroll}>
            {visibleResults.length === 0 ? (
              <div className={classes.message}>
                {loading
                  ? 'Searching…'
                  : results.length > 0
                  ? 'All matching delegates added'
                  : 'No delegates found'}
              </div>
            ) : (
              visibleResults.map((delegate) => (
                <div
                  key={delegate.uuid}
                  className={classes.row}
                  onClick={() => add(delegate)}
                >
                  <ProfileIcon
                    name={delegate.key}
                    url={delegate.profileImageUrl ?? undefined}
                    size={30}
                  />
                  <span className={classes.name}>{delegate.key}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </AnchoredPanel>
    </div>
  );
}

export default BookingDelegatePicker;
